import React, { useState } from "react";
import "./css/refinance_tca_graphs.scss";
import { useDocument } from "../../loan_tools/loan_tool";
import { useUser } from "../../../hooks/user";
import { loan_visible_for_viewer } from "../../../constants/lender_visibility";
import DateField, { today } from "../../fields/date_field";
import PhoneField from "../../fields/phone_field";
import Image from "../../ui/image";
import { borrower_names } from "../../../constants/utils";
import { savings_with_new_loan, total_closing_costs } from "../../../calculations/loan";
import { return_on_investment_cash_flow } from "../../../calculations/total_cost_analysis";
import Decimal from "decimal.js";

const COLORS      = ["#006296", "#2980b9", "#8e44ad", "#d35400", "#c0392b"];
const GREEN       = "#27ae60";
const YEAR_OPTIONS = Array.from({ length: 30 }, (_, i) => i + 1);
const LINE_PTS    = [0, 1, 2, 3, 5, 7, 10];
const LINE_DASHES = ["", "6,3", "3,3", "8,4", "4,4"];

function fmtD(d) {
  const n = d instanceof Decimal ? d.toNumber() : Number(d);
  return (n < 0 ? "-$" : "$") + Math.round(Math.abs(n)).toLocaleString();
}

function buildStats(documentType, loan, property, pts, isp, accS, res) {
  const monthly  = savings_with_new_loan(documentType, pts, accS, res, loan, property, isp);
  const closing  = total_closing_costs(documentType, loan, property, pts, isp);
  const annualCF = monthly.mul(12);
  const monthsRecoup = closing.gt(0) && monthly.gt(0)
    ? closing.div(monthly) : new Decimal(999);
  const roi = closing.gt(0) && annualCF.gt(0)
    ? annualCF.mul(100).div(closing) : new Decimal(0);
  const rate = parseFloat(loan.rate || 0);
  function netAt(yr) { return annualCF.mul(yr).sub(closing); }
  return { monthly, closing, annualCF, monthsRecoup, roi, rate, netAt };
}

// ── Net Savings Bar ───────────────────────────────────────────────────────────

function NetSavingsBar({ data, bestIdx, stayYears }) {
  if (!data.length) return null;
  const W = 860, H = 200, pad = { l: 70, r: 20, t: 28, b: 32 };
  const vals      = data.map(d => d.netAt(stayYears).toNumber());
  const actualMin = Math.min(...vals);
  const actualMax = Math.max(...vals);
  const delta     = (actualMax - actualMin) || 1;
  const p         = Math.max(delta * 0.35, 500);
  const axisMax   = actualMax + p;
  const axisMin   = actualMin >= 0 ? Math.max(0, actualMin - p) : actualMin - p;
  const range     = axisMax - axisMin;
  const barW      = Math.floor((W - pad.l - pad.r) / data.length) - 10;
  const yOf       = v => pad.t + H * (axisMax - v) / range;
  const zeroY     = yOf(0);
  const showZero  = zeroY >= pad.t && zeroY <= pad.t + H;

  return (
    <svg className="g3-bar-svg" viewBox={`0 0 ${W} ${H + pad.t + pad.b}`} width="100%">
      {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const v = axisMin + f * range;
        const y = yOf(v);
        return (
          <g key={i}>
            <line x1={pad.l} y1={y} x2={W - pad.r} y2={y}
              stroke={showZero && Math.abs(v) < range * 0.01 ? "rgba(0,0,0,0.25)" : "#eee"} strokeWidth="1" />
            <text x={pad.l - 5} y={y + 4} textAnchor="end" fontSize="9" fill="#999">
              {v < 0 ? "-$" : "$"}{Math.round(Math.abs(v) / 1000)}k
            </text>
          </g>
        );
      })}
      {showZero && (
        <line x1={pad.l} y1={zeroY} x2={W - pad.r} y2={zeroY}
          stroke="rgba(0,0,0,0.2)" strokeWidth="1" />
      )}
      {data.map((d, i) => {
        const x        = pad.l + i * (barW + 10) + 5;
        const v        = vals[i];
        const color    = i === bestIdx ? GREEN : COLORS[i];
        const barFloor = showZero ? 0 : axisMin;
        const vY       = yOf(v);
        const floorY   = yOf(barFloor);
        const rectY    = Math.min(vY, floorY);
        const bH       = Math.max(Math.abs(vY - floorY), 2);
        const labelY   = v >= barFloor ? vY - 5 : vY + 12;
        return (
          <g key={i}>
            <rect x={x} y={rectY} width={barW} height={bH} fill={color} rx="3" />
            <text x={x + barW / 2} y={labelY}
              textAnchor="middle" fontSize="10" fill={color} fontWeight="bold">
              {fmtD(d.netAt(stayYears))}
            </text>
            <text x={x + barW / 2} y={pad.t + H + 14} textAnchor="middle" fontSize="9" fill="#555">
              Loan {i + 1}
            </text>
            <text x={x + barW / 2} y={pad.t + H + 25} textAnchor="middle" fontSize="9" fill="#999">
              {d.rate.toFixed(3)}%
            </text>
          </g>
        );
      })}
      {axisMin > 0 && (
        <text x={pad.l + (W - pad.l - pad.r) / 2} y={pad.t + H - 4} textAnchor="middle"
          fontSize="8" fill="#ccc" fontStyle="italic">
          axis starts at ${Math.round(axisMin / 1000)}k — zoomed to show differences
        </text>
      )}
    </svg>
  );
}

// ── Breakeven Bar ─────────────────────────────────────────────────────────────

function BreakevenBar({ data, bestBrkIdx }) {
  if (!data.length) return null;
  const W = 860, barH = 30, gap = 10;
  const pad = { l: 100, r: 130, t: 18, b: 22 };
  const H   = data.length * (barH + gap) + pad.t + pad.b;
  const valid = data.map(d => d.monthsRecoup.toNumber()).filter(m => m < 900);
  const maxMo = valid.length ? Math.max(...valid) : 36;

  return (
    <svg className="g3-bar-svg" viewBox={`0 0 ${W} ${H}`} width="100%">
      <text x={pad.l} y={pad.t - 4} fontSize="9" fill="#aaa" textAnchor="middle">0 mo</text>
      {data.map((d, i) => {
        const isBest = i === bestBrkIdx;
        const mo     = d.monthsRecoup.toNumber();
        const bW     = mo >= 900 ? 0 : Math.round((mo / maxMo) * (W - pad.l - pad.r));
        const y      = pad.t + i * (barH + gap);
        const color  = isBest ? GREEN : COLORS[i];
        return (
          <g key={i}>
            <text x={pad.l - 5} y={y + barH / 2 + 4} textAnchor="end" fontSize="10" fill="#555">
              Loan {i + 1} — {d.rate.toFixed(3)}%
            </text>
            <rect x={pad.l} y={y} width={Math.max(bW, 2)} height={barH} fill={color} rx="3" />
            <text x={pad.l + bW + 5} y={y + barH / 2 + 4} fontSize="10" fill={color} fontWeight="bold">
              {mo >= 900 ? "N/A" : mo.toFixed(1) + " mo" + (isBest ? " ✓" : "")}
            </text>
          </g>
        );
      })}
      <text x={W / 2} y={H - 4} textAnchor="middle" fontSize="9" fill="#aaa">
        Months to recoup closing costs (shorter bar = faster payback)
      </text>
    </svg>
  );
}

// ── Net Savings Line ──────────────────────────────────────────────────────────

function NetSavingsLine({ data, bestIdx, stayYears }) {
  if (!data.length) return null;
  const W = 860, H = 220, pad = { l: 75, r: 20, t: 20, b: 40 };
  const allV  = data.flatMap(d => LINE_PTS.map(p => d.netAt(p).toNumber()));
  const minV  = Math.min(0, ...allV);
  const maxV  = Math.max(0, ...allV);
  const range = maxV - minV || 1;

  function xP(yr) { return pad.l + (yr / LINE_PTS[LINE_PTS.length - 1]) * (W - pad.l - pad.r); }
  function yP(v)  { return pad.t + H - ((v - minV) / range) * H; }
  const zeroY = yP(0);

  return (
    <svg className="g3-line-svg" viewBox={`0 0 ${W} ${H + pad.t + pad.b}`} width="100%">
      {[-0.25, 0, 0.25, 0.5, 0.75, 1].map((f, i) => {
        const v = minV + f * range;
        const y = yP(v);
        if (y < pad.t - 5 || y > pad.t + H + 5) return null;
        const isZero = Math.abs(v) < range * 0.02;
        return (
          <g key={i}>
            <line x1={pad.l} y1={y} x2={W - pad.r} y2={y}
              stroke={isZero ? "rgba(0,0,0,0.3)" : "#eee"}
              strokeWidth={isZero ? 1.5 : 1} />
            <text x={pad.l - 5} y={y + 4} textAnchor="end" fontSize="9"
              fill={isZero ? "#555" : "#aaa"} fontWeight={isZero ? "bold" : "normal"}>
              {isZero ? "$0" : (v < 0 ? "-$" : "$") + Math.round(Math.abs(v) / 1000) + "k"}
            </text>
          </g>
        );
      })}
      {LINE_PTS.map(yr => (
        <text key={yr} x={xP(yr)} y={pad.t + H + 14} textAnchor="middle" fontSize="9" fill="#aaa">
          {yr}yr
        </text>
      ))}
      <text x={xP(5)} y={pad.t + H + 27} textAnchor="middle" fontSize="9" fill="#777">
        Years in loan
      </text>
      {stayYears <= 10 && (
        <>
          <line x1={xP(stayYears)} y1={pad.t} x2={xP(stayYears)} y2={pad.t + H}
            stroke="#bbb" strokeWidth="1" strokeDasharray="4,3" />
          <text x={xP(stayYears)} y={pad.t - 6} textAnchor="middle" fontSize="9" fill="#888">
            {stayYears}yr
          </text>
        </>
      )}
      {data.map((d, i) => {
        const pts = LINE_PTS.map(yr => `${xP(yr)},${yP(d.netAt(yr).toNumber())}`).join(" ");
        return (
          <polyline key={i} points={pts} fill="none" stroke={COLORS[i]}
            strokeWidth={i === bestIdx ? 2.5 : 1.8}
            strokeDasharray={LINE_DASHES[i]} strokeLinejoin="round" />
        );
      })}
      {zeroY > pad.t && zeroY < pad.t + H && data.map((d, i) => {
        const breakYr = d.monthsRecoup.toNumber() / 12;
        if (breakYr > 10 || breakYr <= 0 || d.monthsRecoup.toNumber() >= 900) return null;
        return (
          <circle key={i} cx={xP(breakYr)} cy={zeroY} r="4"
            fill={COLORS[i]} stroke="white" strokeWidth="1.5" />
        );
      })}
      <g transform={`translate(${pad.l},${pad.t + H + 34})`}>
        {data.map((d, i) => (
          <g key={i} transform={`translate(${i * 148},0)`}>
            <line x1="0" y1="0" x2="18" y2="0" stroke={COLORS[i]} strokeWidth="2"
              strokeDasharray={LINE_DASHES[i]} />
            <text x="22" y="4" fontSize="9" fill="#555">
              Loan {i + 1} {d.rate.toFixed(3)}%
            </text>
          </g>
        ))}
      </g>
    </svg>
  );
}

// ── ROI Donut Charts ──────────────────────────────────────────────────────────

function ROIDonutCharts({ data, bestRoiIdx }) {
  return (
    <div className="g3-donuts">
      {data.map((d, i) => {
        const pct    = Math.min(d.roi.toNumber(), 100);
        const filled = Math.round(pct / 100 * 360);
        const isBest = i === bestRoiIdx;
        return (
          <div key={i} className={`g3-donut-card${isBest ? " g3-best" : ""}`}
            style={{ borderColor: isBest ? GREEN : "#dde" }}>
            <div className="g3-donut-name" style={{ color: isBest ? GREEN : COLORS[i] }}>
              Loan {i + 1}{isBest ? " ★" : ""}
            </div>
            <div className="g3-donut-rate">{d.rate.toFixed(3)}%</div>
            <div className="g3-donut-ring-wrap">
              <div className="g3-donut-ring"
                style={{ background: `conic-gradient(${isBest ? GREEN : COLORS[i]} ${filled}deg, #dde ${filled}deg)` }}>
                <div className="g3-donut-center">
                  <div className="g3-donut-total">{d.roi.toFixed(2)}%<br />ROI</div>
                </div>
              </div>
            </div>
            <div className="g3-donut-monthly">{fmtD(d.annualCF)}/yr</div>
            <div className="g3-card-label">annual cash flow</div>
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function RefinanceTcaGraphs({ updateSummary }) {
  const [{ companyProfile, accountProfile }] = useUser();
  const [{
    loans, property, property_to_be_sold, is_selling_property,
    accountSummaries, residences, summary, applicant
  }, , { documentType, documentAccountProfile }] = useDocument();

  const [graphType,    setGraphType]    = useState("savings");
  const [graphYearIdx, setGraphYearIdx] = useState(
    ((summary && summary.refinance_cash_flow_over_years) || [3, 5, 7, 10]).length - 1
  );

  const yearCols = (summary && summary.refinance_cash_flow_over_years) || [3, 5, 7, 10];
  const safeIdx  = Math.min(graphYearIdx, yearCols.length - 1);
  const stayYears = yearCols[safeIdx];

  function setYearCols(updated) {
    updateSummary({ refinance_cash_flow_over_years: updated });
  }

  const borrowers      = (applicant && applicant.borrowers) || [];
  const firstBorrower  = borrowers[0] || {};
  const borrowerNames  = borrower_names(borrowers);
  const loName = [documentAccountProfile.first_name, documentAccountProfile.last_name]
    .filter(Boolean).join(" ");

  const activeLoans = (loans || []).filter(
    l => l && loan_visible_for_viewer(l, accountProfile)
  );
  const data = activeLoans.map(loan =>
    buildStats(documentType, loan, property, property_to_be_sold, is_selling_property, accountSummaries, residences)
  );

  const netVals  = data.map(d => d.netAt(stayYears).toNumber());
  const bestNetIdx = netVals.length ? netVals.indexOf(Math.max(...netVals)) : 0;

  const brkVals  = data.map(d => d.monthsRecoup.toNumber());
  const bestBrkIdx = brkVals.length ? brkVals.indexOf(Math.min(...brkVals)) : 0;

  const roiVals  = data.map(d => d.roi.toNumber());
  const bestRoiIdx = roiVals.length ? roiVals.indexOf(Math.max(...roiVals)) : 0;

  const showYearPicker = graphType === "savings" || graphType === "net";

  return (
    <div className="refinance-tca-graphs">

      {/* ── Page Header ────────────────────────────────────────────────────── */}
      <div className="pa-header">
        <div className="pa-logo">
          <Image className="company-logo" url="company/company-logo" />
        </div>
        <div className="pa-borrower-info">
          <div className="pa-section-label">CLIENT(S)</div>
          <div className="pa-borrower-grid">
            <div className="pa-borrower-col">
              {borrowerNames[0] && <div className="pa-name">{borrowerNames[0]}</div>}
              {borrowerNames[1] && <div className="pa-name">{borrowerNames[1]}</div>}
            </div>
            {(borrowerNames[2] || borrowerNames[3]) && (
              <div className="pa-borrower-col">
                {borrowerNames[2] && <div className="pa-name">{borrowerNames[2]}</div>}
                {borrowerNames[3] && <div className="pa-name">{borrowerNames[3]}</div>}
              </div>
            )}
          </div>
          {firstBorrower.current_address && (
            <div className="pa-addr">{firstBorrower.current_address}</div>
          )}
          {firstBorrower.current_address_2 && (
            <div className="pa-addr">{firstBorrower.current_address_2}</div>
          )}
          <div className="pa-addr">
            {[firstBorrower.city, firstBorrower.state].filter(Boolean).join(", ")}
            {firstBorrower.zip_code ? " " + firstBorrower.zip_code : ""}
          </div>
        </div>
        <div className="pa-lo-dates">
          <div className="pa-lo-info">
            {loName && <div className="pa-lo-name">{loName}</div>}
            {documentAccountProfile.nmls_number && (
              <div>NMLS #: {documentAccountProfile.nmls_number}</div>
            )}
            {documentAccountProfile.state_real_estate_license && (
              <div>Lic #: {documentAccountProfile.state_real_estate_license}</div>
            )}
            {documentAccountProfile.direct_contact_phone && (
              <div className="pa-phone-row">
                Cell: <PhoneField naked value={documentAccountProfile.direct_contact_phone} />
              </div>
            )}
            {documentAccountProfile.email && <div>{documentAccountProfile.email}</div>}
          </div>
          <div className="pa-dates">
            <div>
              <span className="pa-date-label">DATE FIRST ISSUED:</span>
              <DateField naked value={applicant && applicant.first_contact_date} />
            </div>
            <div>
              <span className="pa-date-label">DATE LAST ISSUED:</span>
              <DateField naked value={today()} />
            </div>
          </div>
        </div>
      </div>

      {/* ── Title Bar ──────────────────────────────────────────────────────── */}
      <div className="pa-title-block">
        <div className="pa-title">Refinance Loan Comparison Graphs</div>
        <div className="pa-subtitle">
          Visual loan comparison — monthly savings, breakeven timeline, and net savings over time
        </div>
      </div>

      {/* ── Controls (screen only) ─────────────────────────────────────────── */}
      <div className="g3-controls no-print">
        <div className="g3-control-row">
          <label className="g3-label">Years to compare:</label>
          {yearCols.map((yr, i) => (
            <select key={i} className="g3-select g3-select-sm" value={yr}
              onChange={e => {
                const updated = [...yearCols];
                updated[i] = parseInt(e.target.value, 10);
                setYearCols(updated);
              }}>
              {YEAR_OPTIONS.map(y => (
                <option key={y} value={y}>{y} yr{y !== 1 ? "s" : ""}</option>
              ))}
            </select>
          ))}
        </div>
        <div className="g3-control-row">
          <label className="g3-label">Which graph would you like to see?</label>
          <select className="g3-select" value={graphType}
            onChange={e => setGraphType(e.target.value)}>
            <option value="savings">Color bar graph — net savings comparison</option>
            <option value="breakeven">Breakeven timeline — months to recoup closing costs</option>
            <option value="net">Net savings over time — see when each loan pays back</option>
            <option value="roi">Color circle graphs — ROI cash flow by loan</option>
          </select>
          {showYearPicker && (
            <>
              <label className="g3-label">Graph this year:</label>
              <select className="g3-select g3-select-sm" value={stayYears}
                onChange={e => {
                  const newYr = parseInt(e.target.value, 10);
                  const updated = [...yearCols];
                  updated[safeIdx] = newYr;
                  setYearCols(updated);
                }}>
                {YEAR_OPTIONS.map(y => (
                  <option key={y} value={y}>{y} year{y !== 1 ? "s" : ""}</option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {/* ── Loan Cards Strip ───────────────────────────────────────────────── */}
      {data.length > 0 && (
        <div className="g3-loan-strip">
          {data.map((d, i) => (
            <div key={i}
              className={`g3-loan-card${i === bestNetIdx ? " g3-best" : ""}`}
              style={{ borderColor: i === bestNetIdx ? GREEN : "#dde" }}>
              <div className="g3-card-name"
                style={{ color: i === bestNetIdx ? GREEN : COLORS[i] }}>
                Loan {i + 1}{i === bestNetIdx ? " ★" : ""}
              </div>
              <div className="g3-card-rate">{d.rate.toFixed(3)}%</div>
              <div className="g3-card-payment">{fmtD(d.monthly)}/mo</div>
              <div className="g3-card-label">monthly savings</div>
            </div>
          ))}
        </div>
      )}

      {/* ── Chart Area ─────────────────────────────────────────────────────── */}
      <div className="g3-chart-area">
        {data.length === 0 ? (
          <div className="g3-empty">No loan data found. Add loans to see refinance comparison graphs.</div>
        ) : (
          <>
            <div className="g3-chart-title">
              {graphType === "savings"   && `Net savings after ${stayYears} year${stayYears !== 1 ? "s" : ""} — cumulative cash flow minus closing costs`}
              {graphType === "breakeven" && "Months to recoup closing costs — shorter bar = faster payback"}
              {graphType === "net"       && "Net savings over time — dots mark the breakeven point for each loan"}
              {graphType === "roi"       && "ROI cash flow — annual savings as a percentage of closing costs invested"}
            </div>
            {graphType === "savings"   && <NetSavingsBar   data={data} bestIdx={bestNetIdx}   stayYears={stayYears} />}
            {graphType === "breakeven" && <BreakevenBar    data={data} bestBrkIdx={bestBrkIdx} />}
            {graphType === "net"       && <NetSavingsLine  data={data} bestIdx={bestNetIdx}   stayYears={stayYears} />}
            {graphType === "roi"       && <ROIDonutCharts  data={data} bestRoiIdx={bestRoiIdx} />}
          </>
        )}
      </div>

      {/* ── Best Loan Callout ──────────────────────────────────────────────── */}
      {data.length > 0 && (
        <div className="g3-best-callout">
          <strong>
            ★ Best net savings at year {stayYears}: Loan {bestNetIdx + 1}
          </strong>
          &nbsp;at {data[bestNetIdx].rate.toFixed(3)}%
          &nbsp;|&nbsp;Monthly savings: {fmtD(data[bestNetIdx].monthly)}/mo
          &nbsp;|&nbsp;Net savings at {stayYears} year{stayYears !== 1 ? "s" : ""}: {fmtD(data[bestNetIdx].netAt(stayYears))}
          {bestNetIdx !== bestBrkIdx && (
            <>
              <br />
              <span style={{ fontSize: "12px", opacity: 0.85 }}>
                ⚡ Fastest breakeven: Loan {bestBrkIdx + 1}
                &nbsp;at {data[bestBrkIdx].monthsRecoup.toFixed(1)} months
                — lower closing costs offset lower monthly savings.
                &nbsp;Best choice depends on how long the client keeps the loan.
              </span>
            </>
          )}
        </div>
      )}

      {/* ── Disclaimer ─────────────────────────────────────────────────────── */}
      <div className="g3-disclaimer">
        Disclaimer: Net savings figures represent estimated cumulative savings minus total closing costs.
        Monthly savings is the difference between the existing and new loan payments.
        Breakeven is the time required to recoup closing costs through monthly savings.
        This analysis is for informational purposes only and does not constitute a loan commitment.
        &copy; {new Date().getFullYear()} The LANIS Group, LLC.
      </div>

    </div>
  );
}
